import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, Field, Notice, OnOffBadge, SkeletonList, SkeletonTable, Switch } from '@/admin/AdminShell'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  checkConnection,
  createSubscription,
  deleteSubscription,
  fetchConnections,
  fetchDeliveries,
  fetchDelivery,
  fetchPush,
  fetchSubscriptions,
  fetchWebhookCatalog,
  generateVapidKeys,
  pingSubscription,
  retryDelivery,
  rotateSecret,
  updatePush,
  updateSubscription,
  type CheckResult,
  type Connection,
  type Delivery,
  type DeliveryStatus,
  type PingResult,
  type PushChannel,
  type PushView,
  type WebhookEvent,
  type WebhookSubscription,
} from './integrationsApi'

/**
 * Интеграции (тикет #124): сводка всех подключений, push-каналы и исходящие вебхуки.
 *
 * Настройки, у которых есть свой экран (почта, ИИ, телефония), здесь не повторяются — сводка
 * ведёт туда ссылкой: две формы одного подключения разошлись бы при первой правке. Своего
 * экрана нет у push-каналов (#120) — их форма здесь. Мессенджеры (#125) и заглушки (#56)
 * показываются строкой без формы.
 *
 * Секрет подписки вебхука сервер показывает **один раз** — в ответе на создание и на
 * перевыпуск. Экран держит его только в состоянии этого ответа и говорит об этом словами:
 * перезагрузка страницы его стирает, и узнать его снова нельзя, только перевыпустить.
 */
export function IntegrationsPage() {
  const { t, user } = useApp()
  const mayRead = can(user, 'system.integration', 'read')
  return (
    <AdminPage titleKey="integrations.title" leadKey="integrations.lead" forbidden={!mayRead}
               readOnly={!can(user, 'system.integration', 'update')}>
      {!mayRead ? (
        <StateView boxed kind="forbidden" title={t('integrations.forbidden')} />
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <ConnectionsPanel />
          <PushPanel />
          <WebhooksPanel />
        </div>
      )}
    </AdminPage>
  )
}

function useMoment() {
  const { locale } = useApp()
  return (value: string | null | undefined) =>
    value == null ? '—' : new Date(value).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}

function failureText(failure: unknown, fallback: string) {
  return failure instanceof ApiError ? failure.message : fallback
}

// ------------------------------------------------------------------ connections

function ConnectionsPanel() {
  const { t, user } = useApp()
  const mayEdit = can(user, 'system.integration', 'update')
  const queryClient = useQueryClient()
  const moment = useMoment()
  const connections = useQuery({ queryKey: ['admin-integrations'], queryFn: ({ signal }) => fetchConnections(signal) })
  const [checked, setChecked] = useState<Record<string, CheckResult | string>>({})
  const check = useMutation({
    mutationFn: (code: string) => checkConnection(code),
    onSuccess: (result, code) => {
      setChecked((previous) => ({ ...previous, [code]: result }))
      void queryClient.invalidateQueries({ queryKey: ['admin-integrations'] })
    },
    onError: (failure, code) => setChecked((previous) => ({ ...previous, [code]: failureText(failure, t('app.error')) })),
  })

  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('integrations.connections')}>
      <span className="h2">{t('integrations.connections')}</span>
      {connections.isLoading ? (
        <SkeletonTable columns={5} rows={6} />
      ) : connections.error ? (
        <StateView kind="error" compact title={t('integrations.error')} />
      ) : (
        <table className="card-table" aria-label={t('integrations.connections')}>
          <thead>
            <tr>
              <th>{t('integrations.col.name')}</th>
              <th>{t('integrations.col.state')}</th>
              <th>{t('integrations.col.credentials')}</th>
              <th>{t('integrations.col.check')}</th>
              <th>{t('integrations.col.where')}</th>
            </tr>
          </thead>
          <tbody>
            {(connections.data ?? []).map((connection) => {
              const result = checked[connection.code]
              return (
                <tr key={connection.code}>
                  <td>
                    <span className="strong">{connection.title}</span>
                    <div className="t-sm muted mono">{connection.code}</div>
                  </td>
                  <td><OnOffBadge on={connection.enabled} /></td>
                  <td>{connection.credentialsSet ? t('integrations.credentials.set') : t('integrations.credentials.unset')}</td>
                  <td>
                    <LastCheck connection={connection} moment={moment} />
                    {typeof result === 'string' && <div className="t-sm" role="alert">{result}</div>}
                    {connection.checkable && mayEdit && (
                      <button type="button" className="btn btn-ghost btn-sm" disabled={check.isPending}
                              onClick={() => check.mutate(connection.code)}>
                        <Icon name="refresh" size="sm" />
                        {t('integrations.check')}
                      </button>
                    )}
                  </td>
                  <td><Where connection={connection} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </section>
  )
}

function LastCheck({ connection, moment }: { connection: Connection; moment: (value: string | null | undefined) => string }) {
  const { t } = useApp()
  if (connection.lastCheckedAt == null) {
    return <span className="muted">{connection.checkable ? t('integrations.check.never') : t('integrations.check.none')}</span>
  }
  return (
    <div>
      <span className={connection.lastCheckOk ? 'badge b-success' : 'badge b-danger'}>
        {connection.lastCheckOk ? t('integrations.check.ok') : t('integrations.check.failed')}
      </span>{' '}
      <span className="t-sm muted">{moment(connection.lastCheckedAt)}</span>
      {connection.lastCheckMessage != null && <div className="t-sm">{connection.lastCheckMessage}</div>}
    </div>
  )
}

function Where({ connection }: { connection: Connection }) {
  const { t } = useApp()
  switch (connection.kind) {
    case 'SCREEN':
      return connection.screen ? <Link to={connection.screen}>{t('integrations.where.screen')}</Link> : <span>—</span>
    case 'PUSH':
      return <a href="#integrations-push">{t('integrations.where.push')}</a>
    case 'MESSENGER':
      return <span className="muted">{t('integrations.where.messenger')}</span>
    case 'STUB':
      return <span className="muted">{t('integrations.where.stub')}</span>
    case 'OTHER':
      return <span className="muted">{t('integrations.where.other')}</span>
  }
}

// ------------------------------------------------------------------ push

/** Несекретные поля каналов — те же, что принимает `PushSettingsService`. */
const PUSH_FIELDS: Record<PushChannel, string[]> = {
  webpush: ['publicKey', 'subject'],
  fcm: ['projectId', 'clientEmail', 'endpoint', 'tokenUri'],
  rustore: ['projectId', 'endpoint'],
}

function PushPanel() {
  const { t } = useApp()
  const push = useQuery({ queryKey: ['admin-push'], queryFn: ({ signal }) => fetchPush(signal) })
  return (
    <section id="integrations-push" className="panel stack" style={{ gap: 12 }} aria-label={t('integrations.push')}>
      <span className="h2">{t('integrations.push')}</span>
      <p className="t-sm muted">{t('integrations.push.lead')}</p>
      {push.isLoading ? (
        <SkeletonList rows={3} />
      ) : push.error ? (
        <StateView kind="error" compact title={t('integrations.error')} />
      ) : (
        (push.data ?? []).map((view) => <PushForm key={view.channel} view={view} />)
      )}
    </section>
  )
}

function PushForm({ view }: { view: PushView }) {
  const { t, user } = useApp()
  const mayEdit = can(user, 'system.integration', 'update')
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(view.enabled)
  const [fields, setFields] = useState<Record<string, string>>(view.settings ?? {})
  const [secret, setSecret] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    setEnabled(view.enabled)
    setFields(view.settings ?? {})
  }, [view])

  const done = (value: PushView) => {
    setNotice(null)
    setSecret('')
    setSaved(true)
    queryClient.setQueryData<PushView[]>(['admin-push'], (list) =>
      (list ?? []).map((other) => (other.channel === value.channel ? value : other)))
    void queryClient.invalidateQueries({ queryKey: ['admin-integrations'] })
  }
  const save = useMutation({
    mutationFn: () => updatePush(view.channel, {
      enabled,
      settings: Object.fromEntries(Object.entries(fields).filter(([, value]) => value.trim() !== '')),
      secret: secret === '' ? undefined : secret,
    }),
    onSuccess: done,
    onError: (failure) => { setSaved(false); setNotice(failureText(failure, t('app.error'))) },
  })
  const keys = useMutation({
    mutationFn: () => generateVapidKeys(fields.subject?.trim() || null),
    onSuccess: done,
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  const id = `push-${view.channel}`
  return (
    <form className="stack" style={{ gap: 8 }} aria-label={t(`integrations.push.${view.channel}`)}
          onSubmit={(event) => { event.preventDefault(); if (mayEdit) save.mutate() }}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h3">{t(`integrations.push.${view.channel}`)}</span>
        <OnOffBadge on={view.enabled} />
        {view.enabled && !view.usable && <span className="badge b-warning">{t('integrations.push.incomplete')}</span>}
      </div>
      <Switch checked={enabled} disabled={!mayEdit} label={t('integrations.push.enabled')}
              onChange={(value) => { setSaved(false); setEnabled(value) }} />
      <div className="form-grid">
        {PUSH_FIELDS[view.channel].map((name) => (
          <Field key={name} label={t(`integrations.push.field.${name}`)} htmlFor={`${id}-${name}`}>
            <input id={`${id}-${name}`} className="input mono" disabled={!mayEdit} value={fields[name] ?? ''}
                   onChange={(event) => { setSaved(false); setFields({ ...fields, [name]: event.target.value }) }} />
          </Field>
        ))}
        <Field label={t(`integrations.push.secret.${view.channel}`)} htmlFor={`${id}-secret`}
               help={view.secretSet ? t('integrations.secret.kept') : t('integrations.secret.unset')}>
          {view.channel === 'fcm' ? (
            <textarea id={`${id}-secret`} className="input mono" rows={3} disabled={!mayEdit} value={secret}
                      autoComplete="off" onChange={(event) => { setSaved(false); setSecret(event.target.value) }} />
          ) : (
            <input id={`${id}-secret`} className="input" type="password" autoComplete="new-password" disabled={!mayEdit}
                   value={secret} onChange={(event) => { setSaved(false); setSecret(event.target.value) }} />
          )}
        </Field>
      </div>
      <Notice text={notice} />
      {saved && <span className="t-sm muted" role="status">{t('integrations.saved')}</span>}
      {mayEdit && (
        <div className="row" style={{ gap: 8 }}>
          <button type="submit" className="btn btn-primary btn-sm" disabled={save.isPending}>
            {save.isPending ? t('adm.saving') : t('admin.save')}
          </button>
          {view.channel === 'webpush' && (
            <button type="button" className="btn btn-secondary btn-sm" disabled={keys.isPending}
                    onClick={() => keys.mutate()}>
              {t('integrations.push.vapid')}
            </button>
          )}
        </div>
      )}
    </form>
  )
}

// ------------------------------------------------------------------ webhooks

const RETRYABLE: DeliveryStatus[] = ['failed', 'rejected', 'abandoned']

function WebhooksPanel() {
  const { t, user } = useApp()
  const mayEdit = can(user, 'system.integration', 'update')
  const queryClient = useQueryClient()
  const catalog = useQuery({ queryKey: ['admin-webhook-catalog'], queryFn: ({ signal }) => fetchWebhookCatalog(signal) })
  const subscriptions = useQuery({ queryKey: ['admin-webhooks'], queryFn: ({ signal }) => fetchSubscriptions(signal) })
  const [editing, setEditing] = useState<WebhookSubscription | 'new' | null>(null)
  const [secret, setSecret] = useState<{ name: string; value: string } | null>(null)
  const [ping, setPing] = useState<{ id: string; result: PingResult } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [filter, setFilter] = useState<string | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-webhooks'] })
    void queryClient.invalidateQueries({ queryKey: ['admin-webhook-deliveries'] })
  }
  const rotate = useMutation({
    mutationFn: (id: string) => rotateSecret(id),
    onSuccess: (value) => { setSecret({ name: value.subscription.name, value: value.secret }); refresh() },
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: refresh,
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  const pinger = useMutation({
    mutationFn: (id: string) => pingSubscription(id),
    onSuccess: (result, id) => setPing({ id, result }),
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  const labels = new Map((catalog.data ?? []).map((event) => [event.type, event.label]))

  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('integrations.webhooks')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('integrations.webhooks')}</span>
        <span className="spacer" />
        {mayEdit && editing == null && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setSecret(null); setEditing('new') }}>
            <Icon name="plus" size="sm" />
            {t('integrations.webhooks.add')}
          </button>
        )}
      </div>
      <p className="t-sm muted">{t('integrations.webhooks.lead')}</p>
      {secret && (
        <div className="alert alert-warning" role="note" aria-label={t('integrations.webhooks.secret')}>
          <Icon name="lock" />
          <span>
            {t('integrations.webhooks.secret.once', { name: secret.name })}{' '}
            <code className="mono" data-testid="webhook-secret">{secret.value}</code>
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSecret(null)}>
            {t('integrations.webhooks.secret.hide')}
          </button>
        </div>
      )}
      <Notice text={notice} />
      {editing != null && (
        <SubscriptionForm initial={editing === 'new' ? null : editing} catalog={catalog.data ?? []}
                          onClose={() => setEditing(null)}
                          onCreated={(name, value) => setSecret({ name, value })}
                          onSaved={refresh} />
      )}
      {subscriptions.isLoading ? (
        <SkeletonTable columns={5} rows={3} />
      ) : subscriptions.error ? (
        <StateView kind="error" compact title={t('integrations.error')} />
      ) : (subscriptions.data ?? []).length === 0 ? (
        <p className="muted">{t('integrations.webhooks.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('integrations.webhooks.subscriptions')}>
          <thead>
            <tr>
              <th>{t('integrations.webhooks.name')}</th>
              <th>{t('integrations.webhooks.events')}</th>
              <th>{t('integrations.col.state')}</th>
              <th>{t('integrations.webhooks.stats')}</th>
              {mayEdit && <th aria-label={t('integrations.webhooks.actions')} />}
            </tr>
          </thead>
          <tbody>
            {(subscriptions.data ?? []).map((subscription) => (
              <tr key={subscription.id}>
                <td>
                  <span className="strong">{subscription.name}</span>
                  <div className="t-sm muted mono">{subscription.url}</div>
                  {ping?.id === subscription.id && (
                    <div className="t-sm" role="status">
                      {t(`integrations.webhooks.ping.${ping.result.result}`)}
                      {ping.result.detail != null && `: ${ping.result.detail}`}
                    </div>
                  )}
                </td>
                <td>{subscription.events.map((type) => labels.get(type) ?? type).join(', ')}</td>
                <td><OnOffBadge on={subscription.active} /></td>
                <td className="t-sm">
                  {t('integrations.webhooks.counts', {
                    delivered: subscription.delivered, waiting: subscription.waiting, lost: subscription.lost,
                  })}
                  <div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFilter(subscription.id)}>
                      {t('integrations.webhooks.journal')}
                    </button>
                  </div>
                </td>
                {mayEdit && (
                  <td>
                    <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSecret(null); setEditing(subscription) }}>
                        {t('integrations.webhooks.edit')}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={pinger.isPending}
                              onClick={() => pinger.mutate(subscription.id)}>
                        {t('integrations.webhooks.ping')}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={rotate.isPending}
                              onClick={() => {
                                if (window.confirm(t('integrations.webhooks.rotate.confirm'))) rotate.mutate(subscription.id)
                              }}>
                        {t('integrations.webhooks.rotate')}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={remove.isPending}
                              onClick={() => {
                                if (window.confirm(t('integrations.webhooks.delete.confirm'))) remove.mutate(subscription.id)
                              }}>
                        {t('integrations.webhooks.delete')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <DeliveriesJournal subscriptionId={filter} subscriptions={subscriptions.data ?? []}
                         onClearFilter={() => setFilter(null)} labels={labels} />
    </section>
  )
}

function SubscriptionForm({ initial, catalog, onClose, onCreated, onSaved }: {
  initial: WebhookSubscription | null
  catalog: WebhookEvent[]
  onClose: () => void
  onCreated: (name: string, secret: string) => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const [name, setName] = useState(initial?.name ?? '')
  const [url, setUrl] = useState(initial?.url ?? '')
  const [events, setEvents] = useState<string[]>(initial?.events ?? [])
  const [active, setActive] = useState(initial?.active ?? true)
  const [notice, setNotice] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), url: url.trim(), events, active }
      if (initial == null) {
        const created = await createSubscription(body)
        onCreated(created.subscription.name, created.secret)
      } else {
        await updateSubscription(initial.id, body)
      }
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  const id = initial ? `hook-${initial.id}` : 'hook-new'
  return (
    <form className="stack panel" style={{ gap: 8 }} aria-label={t(initial ? 'integrations.webhooks.edit' : 'integrations.webhooks.add')}
          onSubmit={(event) => { event.preventDefault(); save.mutate() }}>
      <div className="form-grid">
        <Field label={t('integrations.webhooks.name')} htmlFor={`${id}-name`} required>
          <input id={`${id}-name`} className="input" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label={t('integrations.webhooks.url')} htmlFor={`${id}-url`} required help={t('integrations.webhooks.url.help')}>
          <input id={`${id}-url`} className="input mono" value={url} onChange={(event) => setUrl(event.target.value)} />
        </Field>
      </div>
      <fieldset className="stack" style={{ gap: 4 }}>
        <legend className="label">{t('integrations.webhooks.events')}</legend>
        {catalog.map((event) => (
          <label key={event.type} className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={events.includes(event.type)}
                   onChange={(change) => setEvents(change.target.checked
                     ? [...events, event.type] : events.filter((type) => type !== event.type))} />
            <span>{event.label}</span>
            <span className="t-sm muted mono">{event.type}</span>
          </label>
        ))}
      </fieldset>
      <Switch checked={active} label={t('integrations.webhooks.active')} onChange={setActive} />
      <Notice text={notice} />
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="btn btn-primary btn-sm"
                disabled={save.isPending || name.trim() === '' || url.trim() === '' || events.length === 0}>
          {save.isPending ? t('adm.saving') : t('admin.save')}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('integrations.cancel')}</button>
      </div>
    </form>
  )
}

function DeliveriesJournal({ subscriptionId, subscriptions, onClearFilter, labels }: {
  subscriptionId: string | null
  subscriptions: WebhookSubscription[]
  onClearFilter: () => void
  labels: Map<string, string>
}) {
  const { t } = useApp()
  const moment = useMoment()
  const [status, setStatus] = useState<DeliveryStatus | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const deliveries = useQuery({
    queryKey: ['admin-webhook-deliveries', subscriptionId, status],
    queryFn: ({ signal }) => fetchDeliveries(subscriptionId, status, signal),
  })
  const filtered = subscriptions.find((subscription) => subscription.id === subscriptionId)
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="h3">{t('integrations.journal')}</span>
        {filtered && (
          <span className="badge">
            {filtered.name}
            <button type="button" className="btn btn-ghost btn-sm" aria-label={t('integrations.journal.all')} onClick={onClearFilter}>
              <Icon name="close" size="sm" />
            </button>
          </span>
        )}
        <span className="spacer" />
        <select className="input input-sm" aria-label={t('integrations.journal.status')} value={status ?? ''}
                onChange={(event) => setStatus(event.target.value === '' ? null : event.target.value as DeliveryStatus)}>
          <option value="">{t('integrations.journal.status.any')}</option>
          {(['delivered', 'failed', 'rejected', 'abandoned', 'pending', 'sending'] as DeliveryStatus[]).map((code) => (
            <option key={code} value={code}>{t(`integrations.delivery.${code}`)}</option>
          ))}
        </select>
      </div>
      {deliveries.isLoading ? (
        <SkeletonTable columns={6} rows={4} />
      ) : deliveries.error ? (
        <StateView kind="error" compact title={t('integrations.error')} />
      ) : (deliveries.data ?? []).length === 0 ? (
        <p className="muted">{t('integrations.journal.empty')}</p>
      ) : (
        <table className="card-table" aria-label={t('integrations.journal')}>
          <thead>
            <tr>
              <th>{t('integrations.journal.at')}</th>
              <th>{t('integrations.journal.subscription')}</th>
              <th>{t('integrations.journal.event')}</th>
              <th>{t('integrations.journal.state')}</th>
              <th>{t('integrations.journal.attempts')}</th>
              <th>{t('integrations.journal.next')}</th>
            </tr>
          </thead>
          <tbody>
            {(deliveries.data ?? []).map((delivery) => (
              <DeliveryRow key={delivery.id} delivery={delivery} label={labels.get(delivery.eventType) ?? delivery.eventType}
                           open={open === delivery.id} moment={moment}
                           onToggle={() => setOpen(open === delivery.id ? null : delivery.id)} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function DeliveryRow({ delivery, label, open, onToggle, moment }: {
  delivery: Delivery
  label: string
  open: boolean
  onToggle: () => void
  moment: (value: string | null | undefined) => string
}) {
  const { t } = useApp()
  return (
    <>
      <tr>
        <td>{moment(delivery.createdAt)}</td>
        <td>{delivery.subscriptionName}</td>
        <td>{label}</td>
        <td>
          <span className={STATUS_CLASS[delivery.status]}>{t(`integrations.delivery.${delivery.status}`)}</span>
          {delivery.lastStatusCode != null && <span className="t-sm muted"> HTTP {delivery.lastStatusCode}</span>}
        </td>
        <td>
          <button type="button" className="btn btn-ghost btn-sm" aria-expanded={open} onClick={onToggle}>
            {t('integrations.journal.attemptsCount', { count: delivery.attempts })}
          </button>
        </td>
        <td>{delivery.status === 'failed' || delivery.status === 'pending' ? moment(delivery.retryAfter) : '—'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6}>
            <DeliveryAttempts id={delivery.id} />
          </td>
        </tr>
      )}
    </>
  )
}

const STATUS_CLASS: Record<DeliveryStatus, string> = {
  pending: 'badge',
  sending: 'badge',
  delivered: 'badge b-success',
  failed: 'badge b-warning',
  rejected: 'badge b-danger',
  abandoned: 'badge b-danger',
}

function DeliveryAttempts({ id }: { id: string }) {
  const { t, user } = useApp()
  const mayEdit = can(user, 'system.integration', 'update')
  const moment = useMoment()
  const queryClient = useQueryClient()
  const detail = useQuery({ queryKey: ['admin-webhook-delivery', id], queryFn: ({ signal }) => fetchDelivery(id, signal) })
  const [notice, setNotice] = useState<string | null>(null)
  const retry = useMutation({
    mutationFn: () => retryDelivery(id),
    onSuccess: (value) => {
      setNotice(null)
      queryClient.setQueryData(['admin-webhook-delivery', id], value)
      void queryClient.invalidateQueries({ queryKey: ['admin-webhook-deliveries'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-webhooks'] })
    },
    onError: (failure) => setNotice(failureText(failure, t('app.error'))),
  })
  if (detail.isLoading) return <SkeletonList rows={2} />
  if (detail.error || !detail.data) return <StateView kind="error" compact title={t('integrations.error')} />
  const { delivery, attempts } = detail.data
  return (
    <div className="stack" style={{ gap: 8 }}>
      {delivery.lastError != null && <div className="t-sm">{delivery.lastError}</div>}
      <table className="card-table" aria-label={t('integrations.journal.attemptsTitle')}>
        <thead>
          <tr>
            <th>#</th>
            <th>{t('integrations.journal.at')}</th>
            <th>{t('integrations.journal.result')}</th>
            <th>{t('integrations.journal.code')}</th>
            <th>{t('integrations.journal.answer')}</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map((attempt) => (
            <tr key={attempt.attemptNo}>
              <td>{attempt.attemptNo}</td>
              <td>{moment(attempt.attemptedAt)}</td>
              <td>{t(`integrations.delivery.${attempt.outcome}`)}</td>
              <td>{attempt.statusCode ?? '—'}</td>
              <td className="t-sm">{attempt.detail ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Notice text={notice} />
      {mayEdit && RETRYABLE.includes(delivery.status) && (
        <div>
          <button type="button" className="btn btn-secondary btn-sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
            <Icon name="refresh" size="sm" />
            {t('integrations.journal.retry')}
          </button>
        </div>
      )}
    </div>
  )
}
